
import React, { useMemo, useState } from 'react';
import { Student, AgendaEntry, LearningObjective, UserRole, ActivityType } from '../types';
import { getThemeById } from '../src/themeConfig';
import { 
  Calendar, 
  Clock,
  Target,
  Search,
  ExternalLink,
  BookOpen
} from 'lucide-react';

interface Props {
  student: Student | null;
  agendas: AgendaEntry[];
  objectives: LearningObjective[];
  onNavigate?: (view: any, params?: any) => void;
  onFetchOnDemand?: (target: 'learningObjectives' | 'agendas' | 'learningContents') => Promise<void>;
  appTheme?: string;
}

const isSubjectMatching = (s1?: string, s2?: string) => {
  if (!s1 || !s2) return false;
  return s1.trim().toLowerCase() === s2.trim().toLowerCase();
};

const isClassMatching = (targetClass?: string | null, studentClass?: string | null): boolean => {
  if (!studentClass || !targetClass) return false;
  const clean = (s: string) => s.trim().toLowerCase().replace(/^kelas\s*/i, '').replace(/[\s\-_]+/g, '');
  const normTarget = clean(targetClass);
  const normStudent = clean(studentClass);
  if (!normTarget || !normStudent) return false;
  if (normTarget === normStudent) return true;
  if (normTarget === 'semua' || normTarget === 'semuakelompok' || normTarget === 'semuakelas') return true;
  const targetList = targetClass.split(',').map(s => clean(s));
  if (targetList.includes(normStudent)) return true;
  return false;
};

const StudentAgendaView: React.FC<Props> = ({ 
  student, 
  agendas, 
  objectives,
  onFetchOnDemand,
  appTheme = 'wzc-themes'
}) => {
  if (!student) return null;

  const currentTheme = useMemo(() => getThemeById(appTheme), [appTheme]);
  const [activeTab, setActiveTab] = useState<'tp' | 'agenda'>('agenda');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = React.useRef<string>('');

  React.useEffect(() => {
    if (onFetchOnDemand && student) {
      if (hasFetchedRef.current === student.id) return;
      hasFetchedRef.current = student.id;

      setIsLoading(true);
      Promise.all([
        onFetchOnDemand('agendas'),
        onFetchOnDemand('learningObjectives')
      ]).finally(() => {
        setIsLoading(false);
      });
    }
  }, [student?.id, onFetchOnDemand]);

  const availableSubjects = useMemo(() => {
    if (!student || !student.class) return [];
    const subjectsFromAgendas = (agendas || []).filter(a => isClassMatching(a.class, student.class)).map(a => a.subject);
    const subjectsFromObjectives = (objectives || []).map(o => o.subject);
    return Array.from(new Set([...subjectsFromAgendas, ...subjectsFromObjectives])).filter(Boolean);
  }, [agendas, objectives, student]);

  const filteredAgendas = useMemo(() => {
    return (agendas || [])
      .filter(a => !a.isArchived)
      .filter(a => isClassMatching(a.class, student.class) && (!selectedSubject || isSubjectMatching(a.subject, selectedSubject)))
      .filter(a => {
        const topic = (a.topic || '').toLowerCase();
        const subject = (a.subject || '').toLowerCase();
        const notes = (a.notes || '').toLowerCase();
        const tpDesc = (a.tpDescription || '').toLowerCase();
        const term = (searchTerm || '').toLowerCase();
        return topic.includes(term) || subject.includes(term) || notes.includes(term) || tpDesc.includes(term);
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [agendas, student.class, selectedSubject, searchTerm]);

  const filteredObjectives = useMemo(() => {
    return (objectives || [])
      .filter(o => !selectedSubject || isSubjectMatching(o.subject, selectedSubject))
      .filter(o => {
        const description = (o.description || '').toLowerCase();
        const code = (o.code || '').toLowerCase();
        const scope = (o.scope || '').toLowerCase();
        const term = (searchTerm || '').toLowerCase();
        return description.includes(term) || code.includes(term) || scope.includes(term);
      })
      .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true, sensitivity: 'base' }));
  }, [objectives, selectedSubject, searchTerm]);

  return (
    <div className="animate-fade-in space-y-8 pb-20">
      {/* Header Section */}
      <div className="bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} rounded-2xl flex items-center justify-center text-white shadow-lg shadow-black/10`}>
              <Calendar size={28} />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-slate-800 dark:text-white tracking-tight">Agenda Siswa</h2>
              <p className="text-sm text-slate-500 font-bold">Pantau agenda harian dan tujuan pembelajaran</p>
            </div>
          </div>

          <div className="flex p-1.5 bg-slate-100 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-800 self-start sm:self-center">
            <button
              onClick={() => setActiveTab('agenda')}
              className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                activeTab === 'agenda'
                  ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-md`
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
              }`}
            >
              Agenda Harian
            </button>
            <button
              onClick={() => setActiveTab('tp')}
              className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                activeTab === 'tp'
                  ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-md`
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
              }`}
            >
              TP
            </button>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] shadow-sm border border-slate-200 dark:border-slate-800">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder="Cari agenda atau TP..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 rounded-2xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 transition-all"
            />
          </div>
          <div className="w-full md:w-64">
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="w-full px-4 py-3.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 rounded-2xl text-sm font-black focus:ring-2 focus:ring-indigo-500 transition-all appearance-none cursor-pointer"
            >
              <option value="">Semua Mata Pelajaran</option>
              {availableSubjects.map(sub => (
                <option key={sub} value={sub}>{sub}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content Area */}
      {isLoading ? (
        <div className="py-20 text-center bg-white dark:bg-slate-900 rounded-[3.5rem] border-2 border-dashed border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <Calendar className="text-slate-200" size={64} />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            </div>
            <div>
              <p className="text-lg font-black text-slate-800 dark:text-white">Memuat Data Agenda...</p>
              <p className="text-sm font-medium text-slate-500">Mohon tunggu sebentar.</p>
            </div>
          </div>
        </div>
      ) : activeTab === 'agenda' ? (
        <div className="space-y-6">
          {filteredAgendas.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAgendas.map((agenda) => (
                <div 
                  key={agenda.id} 
                  className={`relative p-6 rounded-[2.5rem] border flex flex-col h-full overflow-hidden group/agenda transition-all duration-300 ${
                    agenda.documentationUrl 
                      ? 'border-transparent shadow-md hover:shadow-xl hover:-translate-y-1' 
                      : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-xl hover:-translate-y-1'
                  }`}
                >
                  {agenda.documentationUrl && (
                    <>
                      <div 
                        className="absolute inset-0 bg-cover bg-center z-0 group-hover/agenda:scale-110 transition-transform duration-700"
                        style={{ backgroundImage: `url(${agenda.documentationUrl})` }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-slate-900/40 z-0" />
                    </>
                  )}
                  
                  <div className="flex-1 relative z-10 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                      <div className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 ${
                        agenda.documentationUrl ? 'bg-white/20 text-white backdrop-blur-sm border border-white/10' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                      }`}>
                        <Calendar size={12} /> {agenda.date}
                      </div>
                      <div className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                        agenda.documentationUrl
                          ? agenda.status === 'Selesai' ? 'bg-emerald-500/80 text-white backdrop-blur-sm border border-emerald-400/20' : 'bg-amber-500/80 text-white backdrop-blur-sm border border-amber-400/20'
                          : agenda.status === 'Selesai' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
                      }`}>
                        {agenda.status}
                      </div>
                    </div>

                    <div className="mb-4">
                      <h4 className={`text-base font-black mb-1 line-clamp-2 ${agenda.documentationUrl ? 'text-white' : 'text-slate-800 dark:text-white'}`}>
                        {agenda.subject}
                      </h4>
                      <p className={`text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 ${agenda.documentationUrl ? 'text-slate-300' : 'text-slate-500 dark:text-slate-400'}`}>
                        <Clock size={12} /> Jam ke {agenda.startPeriod} - {agenda.endPeriod}
                      </p>
                    </div>

                    <div className={`flex-1 mb-6 p-4 rounded-2xl ${agenda.documentationUrl ? 'bg-white/10 backdrop-blur-sm border border-white/5' : 'bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800'}`}>
                      <p className={`text-xs font-bold mb-2 ${agenda.documentationUrl ? 'text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                        {agenda.topic}
                      </p>
                      {agenda.notes && (
                        <p className={`text-[10px] leading-relaxed line-clamp-3 ${agenda.documentationUrl ? 'text-slate-300' : 'text-slate-500 dark:text-slate-400'}`}>
                          {agenda.notes}
                        </p>
                      )}
                      {agenda.activityType && (
                        <div className="mt-3 inline-block px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400">
                          {agenda.activityType}
                        </div>
                      )}
                    </div>

                    {agenda.materialLink && (
                      <div className="mt-auto pt-4 border-t border-slate-200/20 dark:border-slate-700/50">
                        <a 
                          href={agenda.materialLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all text-xs font-black uppercase tracking-widest ${
                            agenda.documentationUrl 
                              ? 'bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm border border-white/10' 
                              : 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-500 transition-colors'
                          }`}
                        >
                          <ExternalLink size={16} />
                          Lihat Materi
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-20 text-center bg-white dark:bg-slate-900 rounded-[3rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
              <Clock className="mx-auto text-slate-300 mb-4" size={56} />
              <p className="text-lg font-black text-slate-400 italic">Belum ada data agenda harian ditemukan</p>
              <p className="text-sm text-slate-500 font-medium">Coba ubah filter atau kata kunci pencarian Anda.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {filteredObjectives.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredObjectives.map((tp) => (
                <div key={tp.id} className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800 hover:shadow-xl hover:-translate-y-1 transition-all group">
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-400 rounded-xl text-xs font-black uppercase tracking-widest border border-indigo-200 dark:border-indigo-800">
                      {tp.code}
                    </span>
                    <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded-xl text-slate-400">
                      <Target size={18} />
                    </div>
                  </div>
                  
                  <h4 className="text-sm font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-2">{tp.subject}</h4>
                  
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 mb-4">
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-200 leading-relaxed">
                      {tp.scope || tp.description}
                    </p>
                  </div>

                  {tp.element && (
                    <div className="flex items-center gap-2">
                       <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></div>
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{tp.element}</p>
                    </div>
                  )}
                  
                  {tp.description && tp.scope && (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                       <p className="text-xs font-medium text-slate-500 dark:text-slate-400 italic">
                         "{tp.description}"
                       </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-20 text-center bg-white dark:bg-slate-900 rounded-[3rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
              <Target className="mx-auto text-slate-300 mb-4" size={56} />
              <p className="text-lg font-black text-slate-400 italic">Belum ada Tujuan Pembelajaran ditemukan</p>
              <p className="text-sm text-slate-500 font-medium">Coba ubah filter atau kata kunci pencarian Anda.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentAgendaView;
